/**
 * LookFeelRefsPanel — Upload reference images + auto-decode look/lighting/color
 * grade into a structured 13-field object that gets injected into every
 * director prompt.
 *
 * Sits at the top of the Shot Breakdown stage BEFORE the input-method picker
 * (AI / Upload / Paste). Uploads flow into project.look_feel_refs, then a
 * debounced auto-decode call to GPT-5.2 vision produces project.look_feel_decoded
 * (the 13-field object). Fields are fully editable — matches Brand DNA UX.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Loader2, X, RefreshCw, Sparkles, Image as ImageIcon, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_REFS = 20;

const FIELD_LABELS = {
  lighting_philosophy: 'Lighting Philosophy',
  key_source: 'Key Source',
  fill_strategy: 'Fill Strategy',
  practical_lights: 'Practical Lights',
  color_science: 'Color Science',
  white_balance: 'White Balance',
  skin_tone_target: 'Skin Tone Target',
  palette: 'Palette',
  contrast: 'Contrast',
  highlights: 'Highlights',
  shadows: 'Shadows',
  texture: 'Texture',
  reference_feel: 'Reference Feel',
};

export function LookFeelRefsPanel({ projectId }) {
  const [refs, setRefs] = useState([]);
  const [decoded, setDecoded] = useState({});
  const [decodedAt, setDecodedAt] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [decoding, setDecoding] = useState(false);
  const [savingField, setSavingField] = useState(null);
  const decodeTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const [refsRes, decodedRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/look-feel-refs`),
        axios.get(`${API}/projects/${projectId}/look-feel-decoded`),
      ]);
      setRefs(refsRes.data?.items || []);
      setDecoded(decodedRes.data?.decoded || {});
      setDecodedAt(decodedRes.data?.decoded_at || null);
    } catch (e) {
      /* silently ignore — new project may 404 */
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const runDecode = useCallback(async (force = false) => {
    if (decoding) return;
    setDecoding(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/look-feel-decode`, { force });
      setDecoded(res.data?.decoded || {});
      setDecodedAt(res.data?.decoded_at || null);
      toast.success('Look & Feel decoded — injected into every keyframe prompt');
      setExpanded(true);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      if (e.response?.status === 409) {
        // User has edits — offer force
        if (window.confirm('You have manual edits on the decoded fields. Overwrite them with a fresh decode?')) {
          return runDecode(true);
        }
      } else {
        toast.error(`Decode failed: ${detail}`);
      }
    } finally {
      setDecoding(false);
    }
  }, [decoding, projectId]);

  // Debounced auto-decode 3s after last successful upload
  const scheduleAutoDecode = useCallback(() => {
    if (decodeTimerRef.current) clearTimeout(decodeTimerRef.current);
    decodeTimerRef.current = setTimeout(() => runDecode(false).catch(() => {}), 3000);
  }, [runDecode]);

  const handleUpload = useCallback(async (files) => {
    if (!files?.length || !projectId) return;
    if (refs.length + files.length > MAX_REFS) {
      toast.error(`Can only hold ${MAX_REFS} refs. Currently have ${refs.length}.`);
      return;
    }
    setUploading(true);
    try {
      for (const file of Array.from(files).slice(0, MAX_REFS - refs.length)) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('use_for', 'lighting,color,mood,texture');
        await axios.post(`${API}/projects/${projectId}/look-feel-refs`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      await load();
      scheduleAutoDecode();
      toast.success(`Uploaded ${files.length} ref${files.length > 1 ? 's' : ''} — decoding in 3s…`);
    } catch (e) {
      toast.error(`Upload failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setUploading(false);
    }
  }, [refs.length, projectId, load, scheduleAutoDecode]);

  const handleDelete = useCallback(async (refId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/look-feel-refs/${refId}`);
      await load();
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
    }
  }, [projectId, load]);

  const handleFieldSave = useCallback(async (field, value) => {
    setSavingField(field);
    try {
      const res = await axios.put(`${API}/projects/${projectId}/look-feel-decoded`, { [field]: value });
      setDecoded(res.data?.decoded || {});
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSavingField(null);
    }
  }, [projectId]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    handleUpload(e.dataTransfer?.files);
  }, [handleUpload]);

  const hasDecoded = Object.keys(FIELD_LABELS).some(k => decoded[k]);

  return (
    <div className="mb-4 rounded-2xl border border-purple-500/20 bg-gradient-to-br from-purple-950/20 to-blue-950/10 p-4" data-testid="look-feel-refs-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-400" />
          <h3 className="text-sm font-semibold text-white/90">Look &amp; Feel References</h3>
          <span className="text-[10px] text-purple-300/60 uppercase tracking-wider">
            Injected into every keyframe prompt · {refs.length}/{MAX_REFS}
          </span>
        </div>
        {hasDecoded && (
          <button
            onClick={() => setExpanded(v => !v)}
            className="text-[11px] text-purple-300/80 hover:text-purple-200 flex items-center gap-1"
            data-testid="look-feel-toggle-expanded"
          >
            {expanded ? <><ChevronUp className="w-3 h-3" /> Collapse decoded</> : <><ChevronDown className="w-3 h-3" /> Show decoded (13 fields)</>}
          </button>
        )}
      </div>

      <p className="text-[11px] text-white/50 mb-3">
        Drop moodboard / stills / reference photos. GPT decodes the look, lighting &amp; color grade —
        every keyframe prompt in the project inherits it.
      </p>

      {/* Upload zone + thumbnails */}
      <div
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={handleDrop}
        className="border-2 border-dashed border-purple-500/30 rounded-xl p-3 min-h-[96px] hover:border-purple-500/50 transition-colors"
      >
        <div className="flex flex-wrap gap-2 items-start">
          {refs.map((r) => (
            <div key={r.id} className="relative group" data-testid={`look-feel-ref-${r.id}`}>
              <img
                src={r.image_url}
                alt="ref"
                className="w-16 h-16 object-cover rounded-lg border border-white/10"
              />
              <button
                onClick={() => handleDelete(r.id)}
                className="absolute -top-1.5 -right-1.5 p-0.5 rounded-full bg-red-500/90 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`look-feel-ref-delete-${r.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {refs.length < MAX_REFS && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-16 h-16 rounded-lg border border-dashed border-purple-400/40 flex flex-col items-center justify-center gap-1 text-purple-300/70 hover:text-purple-200 hover:border-purple-400/70 disabled:opacity-40"
              data-testid="look-feel-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4" /><span className="text-[9px]">Add</span></>}
            </button>
          )}
          {refs.length === 0 && !uploading && (
            <div className="flex-1 flex items-center justify-center text-[11px] text-white/40 min-h-[64px]">
              <ImageIcon className="w-4 h-4 mr-2" /> Drop images here or click Add
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/heic"
          multiple
          className="hidden"
          onChange={(e) => handleUpload(e.target.files)}
          data-testid="look-feel-file-input"
        />
      </div>

      {/* Decode status + trigger */}
      {refs.length > 0 && (
        <div className="flex items-center gap-2 mt-2">
          {decoding && (
            <span className="text-[11px] text-purple-300/80 flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" />
              Decoding look, lighting &amp; grade via GPT-5.2 vision…
            </span>
          )}
          {!decoding && hasDecoded && (
            <span className="text-[11px] text-emerald-300/80">✓ Decoded · injected into all keyframe prompts</span>
          )}
          {!decoding && !hasDecoded && refs.length > 0 && (
            <span className="text-[11px] text-white/50">Ready — auto-decode fires 3s after last upload</span>
          )}
          <button
            onClick={() => runDecode(true)}
            disabled={decoding || refs.length === 0}
            className="ml-auto px-2.5 py-1 rounded-md text-[11px] font-medium bg-purple-500/20 text-purple-200 border border-purple-500/30 hover:bg-purple-500/30 flex items-center gap-1 disabled:opacity-40"
            data-testid="look-feel-decode-btn"
          >
            <RefreshCw className={`w-3 h-3 ${decoding ? 'animate-spin' : ''}`} />
            Re-decode
          </button>
        </div>
      )}

      {/* Decoded 13-field editable panel */}
      {expanded && hasDecoded && (
        <div className="mt-3 pt-3 border-t border-purple-500/15" data-testid="look-feel-decoded-fields">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {Object.entries(FIELD_LABELS).map(([k, label]) => (
              <div key={k}>
                <label className="text-[10px] text-purple-300/70 uppercase tracking-wider flex items-center gap-1">
                  {label}
                  {savingField === k && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
                </label>
                <textarea
                  value={decoded[k] || ''}
                  onChange={(e) => setDecoded(d => ({ ...d, [k]: e.target.value }))}
                  onBlur={(e) => handleFieldSave(k, e.target.value)}
                  rows={2}
                  className="w-full px-2 py-1.5 rounded-md text-[11px] leading-snug bg-white/[0.03] border border-white/[0.08] focus:border-purple-500/40 text-white/75 resize-none outline-none"
                  data-testid={`look-feel-field-${k}`}
                />
              </div>
            ))}
          </div>
          {decodedAt && (
            <p className="text-[9px] text-white/30 mt-2">
              Last decoded: {new Date(decodedAt).toLocaleString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
